import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import tw, { styled } from 'twin.macro'
import { useParams, useNavigate } from 'react-router-dom'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { FiMove } from 'react-icons/fi'
import { CgTrash } from 'react-icons/cg'

import { Skeleton } from 'common/components/Skeleton/Skeleton'
import { useDebouncedTextMutation } from 'common/hooks/useDebouncedTextMutation'
import { useDebounce } from 'common/hooks/useDebounce'
import { useRefsControl } from 'common/components/RefsControl/WorkoutRefsControl/useRefsControl'
import { RefsControlProvider } from 'common/components/RefsControl/WorkoutRefsControl/context'
import { useEventListener } from 'common/hooks/useEventListener'
import { Button } from 'common/components/Button/Button'
import { useAlert } from 'common/components/Alert/hooks/useAlert'
import { Tooltip } from 'common/components/Tooltip/Tooltip'
import { Spinner } from 'common/components/Spinner/Spinner'
import { Sortable } from 'common/components/Sortable/Sortable'
import { Dialog, DialogContent, DialogTrigger } from 'common/components/Dialog/Dialog'
import { CreateDialogBanner } from 'common/components/CreateDialogBanner/CreateDialogBanner'

import useQuizResultsExists from 'modules/AppOnboardingQuiz/hooks/useQuizResultsExists'
import { setIdsOfResultsToDelete, setTitleOfContentToDelete } from 'modules/AppOnboardingQuiz/quizFlowSlice'
import { NEW_USER_QUIZ } from 'modules/App/components/Header'
import { useMonitorAnyWktDragging } from '../hooks/useMonitorAnyWktDragging'
import { dayViewMinHeight, headerMinHeight, titleDescMinHeight } from '../constants/dayViewStyleConstants'
import { getDayCardWidth } from '../utils/dayViewUtils'
import { getIsWorkoutSelected, workoutSelected, workoutsMultiSelected } from '../programSlice'
import {
  useAddExtraWorkoutToDayMutation,
  useDeleteWktOfMultiWorkoutMutation,
  useListenWorkoutQuery,
  useUpdateMultiWktDaysInfoMutation,
} from '../programApi'
import { WktTitleInput, WorkoutDescription, WorkoutSelectedOverlay } from './Workout'
import { DayHeader } from './DayHeader'
import { DayView as DayViewOfSingleWkt } from './DayView'
import { multiWktSortableId } from '../constants/multiWktConstants'
import { weekRenderTypes } from '../programRenderingSlice'
import { DayHeaderContainer } from './styles'

const Container = styled.div(({ isEditing }) => [
  tw`relative flex flex-col bg-white border-l border-r h-full`,
  !isEditing && tw`hover:cursor-pointer`,
  isEditing ? tw`border border-tGray-ml` : tw`border-gray-200`,
])

const DayView = (props) => {
  const {
    mainWktId,
    setEditIdx,
    dayIdx,
    weekIdx,
    workoutIds,
    multiWktsTitle,
    multiWktsInstructions,
    attributes,
    listeners,
    orgId,
    isEditing,
    isEditingSameWk,
    weekWidth,
    renderType,
    setModalWorkout,
    setEditWorkoutInfoModalOpen,
  } = props

  const titleRef = useRef()
  const cardW = getDayCardWidth({
    isEditing,
    isEditingSameWk,
    weekWidth,
  })

  const [selectedWktId, setSelectedWktId] = useState(null)
  const switchToMultiWkts = useCallback(() => setSelectedWktId(null), [])
  const {
    data: mainWorkout = {},
    isLoading,
    isFetching,
  } = useListenWorkoutQuery({
    orgId,
    workoutId: mainWktId,
  })
  const weekDeleting =
    mainWorkout.title === undefined && mainWorkout.dayIdx === undefined && mainWorkout.type === undefined
  const hasLoaded = !isLoading && !isFetching && !mainWorkout.stillLoading && !weekDeleting

  if (hasLoaded && renderType === weekRenderTypes.show) {
    if (selectedWktId) {
      return (
        <DayViewOfSingleWkt
          workoutId={selectedWktId}
          workoutIds={workoutIds}
          dayIdx={dayIdx}
          weekIdx={weekIdx}
          isEditing={isEditing}
          isEditingSameWk={isEditingSameWk}
          setEditIdx={setEditIdx}
          orgId={orgId}
          renderType={renderType}
          weekWidth={weekWidth}
          attributes={attributes}
          listeners={listeners}
          setModalWorkout={setModalWorkout}
          setEditWorkoutInfoModalOpen={setEditWorkoutInfoModalOpen}
          switchToMultiWkts={switchToMultiWkts}
          selectedWktId={selectedWktId}
          setSelectedWktId={setSelectedWktId}
        />
      )
    }

    return (
      <Container
        data-testid='dayContainer'
        className='group'
        style={{ width: cardW, minHeight: dayViewMinHeight }}
        isEditing={isEditing}
        onClick={(e) => {
          if (!isEditing) {
            if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
              titleRef.current.focus()
            }
          }
        }}
      >
        <DayHeader
          dayIdx={dayIdx}
          setEditIdx={setEditIdx}
          workout={mainWorkout}
          isEditing={isEditing}
          dragHandleProps={{ attributes, listeners }}
          orgId={orgId}
          setModalWorkout={setModalWorkout}
          setEditWorkoutInfoModalOpen={setEditWorkoutInfoModalOpen}
          workoutIds={workoutIds}
          mainWktId={mainWktId}
        />
        <RefsControlProvider isEditing={isEditing}>
          <MultiWorkout
            mainWktId={mainWktId}
            mainWorkout={mainWorkout}
            workoutIds={workoutIds}
            multiWktsTitle={multiWktsTitle}
            multiWktsInstructions={multiWktsInstructions}
            isEditing={isEditing}
            dayIdx={dayIdx}
            multiWktDayId={mainWorkout?.multiWktDayId}
            orgId={orgId}
            setEditIdx={setEditIdx}
            setSelectedWktId={setSelectedWktId}
            titleRef={titleRef}
          />
        </RefsControlProvider>
      </Container>
    )
  } else {
    return (
      <Container style={{ width: cardW, minHeight: dayViewMinHeight }}>
        <DayHeaderContainer style={{ minHeight: headerMinHeight }}>
          <Skeleton css={tw`w-10 mt-2`} />
        </DayHeaderContainer>
        <div
          className='flex flex-col justify-center p-4 border-b border-gray-200'
          style={{ minHeight: titleDescMinHeight }}
        >
          <Skeleton css={tw`w-3/6 mb-6`} />
          <Skeleton css={tw`w-5/6`} />
        </div>
        <div className='h-full' />
      </Container>
    )
  }
}

const addWktButtonClasses = tw`
  relative bg-gray-200
  hover:bg-gray-200 text-gray-600
  hover:ring-2 hover:ring-tGray-ml
  rounded-md px-2.5 py-2
  text-xs font-medium
  hover:z-10
`

export function MultiWorkout({
  mainWktId,
  mainWorkout,
  workoutIds = [],
  multiWktsTitle,
  multiWktsInstructions,
  isEditing,
  dayIdx,
  multiWktDayId,
  orgId,
  setEditIdx,
  setSelectedWktId,
  titleRef,
}) {
  const dispatch = useDispatch()
  const descriptionRef = useRef()
  const { setActiveInput } = useRefsControl()
  const { createAlert } = useAlert()
  const { id: programId, workoutId: isLargeView } = useParams()
  const isWktSelected = useSelector((state) => getIsWorkoutSelected(state, mainWktId))
  const isAnyWktDragging = useMonitorAnyWktDragging()

  const [addExtraWktToDay] = useAddExtraWorkoutToDayMutation()
  const [updateMultiWktDaysInfo] = useUpdateMultiWktDaysInfoMutation()

  const [title, setTitle] = useState(multiWktsTitle || '')
  const [instructions, setInstructions] = useState(multiWktsInstructions || '')
  const [isDeletingWkt, setIsDeletingWkt] = useState(false)

  useEffect(() => {
    setTitle(multiWktsTitle)
    setInstructions(multiWktsInstructions)
  }, [multiWktsTitle, multiWktsInstructions])

  const debouncedTitle = useDebounce(title, 500)
  useDebouncedTextMutation({
    stateText: title,
    dbText: multiWktsTitle,
    mutation: updateMultiWktDaysInfo,
    debouncedStateText: debouncedTitle,
    mutationArgs: {
      orgId,
      programId,
      multiWktDayId,
      update: {
        title: debouncedTitle,
      },
    },
  })

  const debouncedInstr = useDebounce(instructions, 500)
  useDebouncedTextMutation({
    stateText: instructions,
    dbText: multiWktsInstructions,
    mutation: updateMultiWktDaysInfo,
    debouncedStateText: debouncedInstr,
    mutationArgs: {
      orgId,
      programId,
      multiWktDayId,
      update: {
        instructions: debouncedInstr,
      },
    },
  })

  const clearEditIdxOnEsc = (e) => {
    if (isEditing && setEditIdx && e.key === 'Escape') {
      setEditIdx(null)
    }
  }

  useEventListener('keydown', clearEditIdxOnEsc)

  const handleCheckboxClick = (e) => {
    e.stopPropagation()

    if (!e.shiftKey) {
      dispatch(
        workoutSelected({
          workout: mainWorkout,
        })
      )
    } else if (e.shiftKey) {
      dispatch(workoutsMultiSelected({ workout: mainWorkout }))
    }
  }

  const wktIdsSortable = workoutIds.map((wktId) => `${wktId}${multiWktSortableId}`)
  return (
    <div
      className='relative flex flex-col flex-1'
      onClick={() => {
        if (!isLargeView) {
          setEditIdx(dayIdx)
        }
      }}
    >
      <WorkoutSelectedOverlay
        data-testid='workoutSelectedOverlay'
        isEditing={isEditing}
        isWktSelected={isWktSelected}
      />
      <div className='relative border-b border-gray-200 min-h-[141px]'>
        <div className='flex items-center px-3 pt-4'>
          {!isLargeView && (
            <input
              data-testid='workoutCheckbox'
              onClick={(e) => handleCheckboxClick(e)}
              checked={isWktSelected ? true : false}
              value={isWktSelected ? 'on' : 'off'}
              type='checkbox'
              className='cursor-pointer rounded-sm w-4.5 h-4.5 border-gray-300 text-tGreen focus:ring-0 hover:border-tGreen ml-[5px]'
              readOnly
              tabIndex={-1}
            />
          )}
          <WktTitleInput
            isLargeView={isLargeView}
            maxLength={22}
            type='text'
            value={title ? title : ''}
            placeholder='Untitled Day'
            onChange={(e) => setTitle(e.target.value)}
            ref={titleRef}
            onFocus={() => {
              if (!isEditing) setEditIdx(dayIdx)
              setActiveInput(titleRef)
            }}
          />
        </div>
        <WorkoutDescription
          instructions={instructions}
          setInstructions={setInstructions}
          ref={descriptionRef}
          onFocus={() => {
            setActiveInput(descriptionRef)
          }}
          placeholder='Day description (Optional)'
        />
      </div>
      <span className='font-medium text-sm mt-[22px] mb-4 mx-2.5'>Workouts</span>
      <SortableContext items={wktIdsSortable} strategy={verticalListSortingStrategy}>
        {wktIdsSortable.map((sortableId, index) => (
          <Sortable
            key={sortableId}
            id={sortableId}
            className='mb-2.5 mx-2.5'
            draggingClasses='opacity-50 border-2 border-tGreen z-10 rounded-md'
            customData={{ mainWktId, wktIdx: index, multiWktDayId, itemType: 'multiWkt' }}
            withHandle={true}
            variableSize={true}
            disabled={{ droppable: isAnyWktDragging }}
          >
            <WorkoutCard
              mainWktId={mainWktId}
              workoutId={sortableId.split(multiWktSortableId)?.[0]}
              workoutIds={workoutIds}
              setSelectedWktId={setSelectedWktId}
              orgId={orgId}
              index={index}
              createAlert={createAlert}
              isDeletingWkt={isDeletingWkt}
              setIsDeletingWkt={setIsDeletingWkt}
            />
          </Sortable>
        ))}
      </SortableContext>
      {isEditing && (
        <div className='flex justify-center my-3'>
          <Button
            size='md'
            css={addWktButtonClasses}
            variant='secondary'
            onClick={() => {
              addExtraWktToDay({ orgId, programId, dayIdx, multiWktDayId, mainWktId })
              createAlert({ text: `Workout added!`, type: 'success' })
            }}
          >
            Add workout
          </Button>
        </div>
      )}
    </div>
  )
}

function WorkoutCard({
  mainWktId,
  workoutId,
  workoutIds,
  setSelectedWktId,
  orgId,
  index,
  createAlert,
  isDeletingWkt,
  setIsDeletingWkt,
  attributes,
  listeners,
}) {
  const dispatch = useDispatch()
  const { id: programId, workoutId: isLargeView } = useParams()
  const navigate = useNavigate()
  const [deleteWktOfMultiWorkout] = useDeleteWktOfMultiWorkoutMutation()

  const { quizResultExists, isQuizResultsLoading } = useQuizResultsExists({
    coachOrgId: orgId,
    itemIds: { [workoutId]: true },
  })

  const {
    data: workout = {},
    isLoading,
    isFetching,
  } = useListenWorkoutQuery({
    orgId,
    workoutId,
  })

  // When deleting week, workouts have only ids and parts
  const weekDeleting = workout.title === undefined && workout.dayIdx === undefined && workout.type === undefined
  const hasLoaded = !isLoading && !isFetching && !workout.stillLoading && !weekDeleting

  return (
    <div
      className='group/inner flex flex-col relative p-3 pr-2 pt-2 rounded-md border shadow-sm hover:border-tGreen cursor-pointer'
      onClick={() => setSelectedWktId(workoutId)}
      disabled={!hasLoaded}
      tabIndex={-1}
    >
      <div className='p-1 rounded bg-gray-100 text-xxs text-gray-400 font-semibold mb-1 leading-none ml-auto'>
        Workout
      </div>
      <div className='relative flex items-center'>
        <div className='font-medium text-sm capitalize'>{index + 1 + '. '}</div>
        <div className='w-full ml-1 truncate'>
          {!hasLoaded ? (
            <Skeleton css={tw`w-1/2`} />
          ) : (
            <h4 className='font-medium text-sm capitalize truncate'>{workout?.title || 'Untitled Workout'}</h4>
          )}
        </div>
      </div>
      {Boolean(workout?.instructions) && hasLoaded && (
        <p className='text-sm text-gray-500 mt-1 line-clamp-2'>{workout?.instructions}</p>
      )}
      <div css={actionContainerClasses} onClick={(e) => e.stopPropagation()}>
        <Tooltip content='Drag within block' triggerClasses='flex'>
          <button {...attributes} {...listeners} tabIndex={-1}>
            <FiMove className='cursor-move w-3.5 h-3.5 my-[3px] text-gray-500 hover:text-tGreen' />
          </button>
        </Tooltip>
        <Tooltip content='Delete' triggerClasses='flex'>
          {quizResultExists ? (
            <Dialog>
              <DialogTrigger onOpenCb={(e) => e.stopPropagation()}>
                <CgTrash className='cursor-pointer w-[18px] h-[18px] text-gray-500 hover:text-tRed ml-1.5' />
              </DialogTrigger>
              <DialogContent>
                <CreateDialogBanner
                  titleText={null}
                  createBtnText='Go to quiz'
                  text='This workout cannot be deleted because it is used as a result in the new user quiz. To delete the workout, please remove it from the quiz first.'
                  handleCreate={() => {
                    dispatch(setIdsOfResultsToDelete({ itemIds: { [workoutId]: true } }))
                    dispatch(setTitleOfContentToDelete(workout.title || 'Untitled Workout'))
                    navigate(NEW_USER_QUIZ)
                  }}
                />
              </DialogContent>
            </Dialog>
          ) : (
            <button
              aria-label='Delete workout'
              disabled={isDeletingWkt}
              tabIndex={-1}
              onClick={async (e) => {
                e.stopPropagation()
                setSelectedWktId(null)
                setIsDeletingWkt(true)
                const { data } = await deleteWktOfMultiWorkout({
                  orgId,
                  programId,
                  workout,
                  workoutIds,
                  mainWktId,
                })
                setIsDeletingWkt(false)
                if (isLargeView && data?.newMainWktId) {
                  navigate(`/programs/${programId}/workouts/${data?.newMainWktId}`, { replace: true })
                }
                createAlert({ text: `Workout deleted!`, type: 'success' })
              }}
            >
              {isDeletingWkt || isQuizResultsLoading ? (
                <Spinner className='w-[18px] h-[18px] text-gray-200 ml-1.5 mb-1' fillClassName='fill-gray-500' />
              ) : (
                <CgTrash className='cursor-pointer w-[18px] h-[18px] text-gray-500 hover:text-tRed ml-1.5' />
              )}
            </button>
          )}
        </Tooltip>
      </div>
    </div>
  )
}

const actionContainerClasses = tw`
  hidden group-hover/inner:flex absolute 
  z-20 right-0 top-1.5 right-1.5 
  items-center py-1 px-1.5 rounded-md 
  border-[1px] bg-white border-gray-200 shadow-sm
`

function areEqual(prevProps, nextProps) {
  const propKeys = Object.keys(nextProps)
  const propsChanged = []

  propKeys.forEach((key) => {
    const isSame = prevProps[key] === nextProps[key]
    if (!isSame) {
      propsChanged.push(key)
    }
  })

  if (propsChanged.length) {
    // console.log('===== DayView.js dayIdx:', nextProps.dayIdx, '===== ')
    // console.log('propsChanged', propsChanged)

    return false
  } else {
    return true
  }
}

export const DayViewMultiWkt = React.memo(DayView, areEqual)
